(function () {
  'use strict';

  const REDUCE_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ============================================================
   * Scroll-driven progress for each panel and reveal block.
   *
   * Progress goes from 0 (element below viewport) → 1 (element
   * fully entered) → stays at 1 while in view → back to 0 when
   * it leaves the top. Used by CSS variables to drive blur,
   * opacity, translateY.
   * ============================================================ */

  class EditorialScroll {
    constructor(root) {
      this.root = root;
      this.targets = Array.from(
        root.querySelectorAll('[data-editorial-panel], [data-editorial-reveal]')
      );
      this.lastUpdate = 0;
      this.rafId = null;
      this.update = this.update.bind(this);
      window.addEventListener('scroll', this.requestUpdate.bind(this), { passive: true });
      window.addEventListener('resize', this.requestUpdate.bind(this));
      this.update();
    }

    requestUpdate() {
      if (this.rafId !== null) return;
      this.rafId = requestAnimationFrame(this.update);
    }

    update() {
      this.rafId = null;
      const vh = window.innerHeight;

      this.targets.forEach((el) => {
        const rect = el.getBoundingClientRect();
        // Reveal starts when top of element crosses 95% of viewport
        // (i.e. it's just barely visible at the bottom edge)
        // Fully revealed only when top reaches 10% of viewport (much more scroll needed).
        const start = vh * 0.95;
        const end = vh * 0.1;
        let progress;

        if (rect.top >= start) {
          progress = 0;
        } else if (rect.top <= end) {
          // Fully revealed; stay clear longer before fading
          if (rect.bottom < vh * 0.25) {
            // Exit fade-out
            const exitT = Math.max(0, Math.min(1, rect.bottom / (vh * 0.25)));
            progress = exitT;
          } else {
            progress = 1;
          }
        } else {
          progress = (start - rect.top) / (start - end);
        }

        el.style.setProperty('--progress', progress.toFixed(3));

        if (progress > 0.38 && !el.classList.contains('is-revealed')) {
          el.classList.add('is-revealed');
        }
      });
    }
  }

  /* ============================================================
   * Draggable image inside .editorial__media
   * - Mouse / touch drag → translate horizontally
   * - Release → snap back to center with easing
   * - Clamped to ±25% of container width
   * ============================================================ */

  class DraggableMedia {
    constructor(el) {
      this.el = el;
      this.img = el.querySelector('.editorial__image, .editorial__image-placeholder');
      if (!this.img) return;

      this.isDown = false;
      this.startX = 0;
      this.currentX = 0;
      this.maxOffset = 0;

      this.onDown = this.onDown.bind(this);
      this.onMove = this.onMove.bind(this);
      this.onUp = this.onUp.bind(this);

      el.addEventListener('mousedown', this.onDown);
      el.addEventListener('touchstart', this.onDown, { passive: true });
      window.addEventListener('mousemove', this.onMove);
      window.addEventListener('touchmove', this.onMove, { passive: false });
      window.addEventListener('mouseup', this.onUp);
      window.addEventListener('touchend', this.onUp);
      window.addEventListener('touchcancel', this.onUp);
    }

    onDown(e) {
      const pt = this.getPoint(e);
      if (!pt) return;
      this.isDown = true;
      this.startX = pt.x - this.currentX;
      this.maxOffset = this.el.offsetWidth * 0.25;
      this.el.classList.add('is-dragging');
      this.el.classList.remove('is-snapping');
    }

    onMove(e) {
      if (!this.isDown) return;
      const pt = this.getPoint(e);
      if (!pt) return;
      // Prevent vertical scroll only if dragging significantly horizontally
      if (e.cancelable && e.type === 'touchmove') {
        const dx = Math.abs(pt.x - (this.startX + this.currentX));
        if (dx > 8) e.preventDefault();
      }
      let dx = pt.x - this.startX;
      // Soft clamp with elastic resistance
      if (dx > this.maxOffset) {
        dx = this.maxOffset + (dx - this.maxOffset) * 0.25;
      } else if (dx < -this.maxOffset) {
        dx = -this.maxOffset + (dx + this.maxOffset) * 0.25;
      }
      this.currentX = dx;
      this.el.style.setProperty('--drag-x', dx.toFixed(0) + 'px');
    }

    onUp() {
      if (!this.isDown) return;
      this.isDown = false;
      this.currentX = 0;
      this.el.classList.remove('is-dragging');
      this.el.classList.add('is-snapping');
      this.el.style.setProperty('--drag-x', '0px');
      setTimeout(() => this.el.classList.remove('is-snapping'), 700);
    }

    getPoint(e) {
      if (e.touches && e.touches[0]) {
        return { x: e.touches[0].clientX };
      }
      if (e.changedTouches && e.changedTouches[0]) {
        return { x: e.changedTouches[0].clientX };
      }
      if (typeof e.clientX === 'number') {
        return { x: e.clientX };
      }
      return null;
    }
  }

  /* ============================================================
   * Boot
   * ============================================================ */

  function init(root) {
    if (root.dataset.editorialInit === 'true') return;
    root.dataset.editorialInit = 'true';

    if (!REDUCE_MOTION) {
      new EditorialScroll(root);
    } else {
      // For reduced motion, force progress = 1 so content is visible
      root.querySelectorAll('[data-editorial-panel], [data-editorial-reveal]').forEach((el) => {
        el.style.setProperty('--progress', '1');
      });
    }

    // Magnetic hover on editorial media — extremely subtle (1.5% of distance)
    root.querySelectorAll('[data-magnetic-hover]').forEach((el) => {
      const STRENGTH = 0.015;
      let raf = null;
      let tx = 0, ty = 0, cx = 0, cy = 0;

      function loop() {
        cx += (tx - cx) * 0.08;
        cy += (ty - cy) * 0.08;
        el.style.transform = `translate(${cx}px, ${cy}px)`;
        if (Math.abs(tx - cx) > 0.05 || Math.abs(ty - cy) > 0.05) {
          raf = requestAnimationFrame(loop);
        } else {
          raf = null;
        }
      }

      el.addEventListener('mousemove', (e) => {
        const rect = el.getBoundingClientRect();
        tx = (e.clientX - (rect.left + rect.width / 2)) * STRENGTH;
        ty = (e.clientY - (rect.top + rect.height / 2)) * STRENGTH;
        if (!raf) raf = requestAnimationFrame(loop);
      });

      el.addEventListener('mouseleave', () => {
        tx = 0; ty = 0;
        if (!raf) raf = requestAnimationFrame(loop);
      });
    });
  }

  function bootAll() {
    document.querySelectorAll('.editorial').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector('.editorial');
    if (root) init(root);
  });
})();
